import React from 'react';
import { Form as FormikForm, Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import media from '../../../utils/mediaQueries';
import Btn from '../../buttons/Btn';
import Field from '../../fields/Field';
import { colors, radius, spacing } from '../tokens';
import { ContactInfo } from '../types';
import {
  Actions, Panel, Reassurance, StepText, StepTitle,
} from './styles';

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 ${spacing.md};
  margin-top: ${spacing.lg};

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const FullWidthRow = styled.div`
  grid-column: 1 / -1;
`;

const CiviliteOptions = styled.div`
  display: flex;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};
`;

const CiviliteOption = styled.label<{ isSelected: boolean }>`
  padding: 10px ${spacing.md};
  border: 1px solid ${({ isSelected }) => (isSelected ? colors.brand : colors.border)};
  border-radius: ${radius.medium};
  background-color: ${({ isSelected }) => (isSelected ? colors.brandSoft : colors.surface)};
  font-size: 14px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  color: ${colors.text};
  cursor: pointer;

  &:focus-within {
    box-shadow: 0 0 0 3px ${colors.brandMedium};
  }

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const FieldLabelText = styled.div<{ hasError: boolean }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  letter-spacing: 0.3px;
  color: ${({ hasError }) => (hasError ? '#f74656' : colors.text)};
`;

const FieldError = styled.div`
  font-size: 12px;
  margin-top: ${spacing.xs};
  color: #f74656;

  &:empty {
    display: none;
  }
`;

const CIVILITE_OPTIONS = [
  { value: 'monsieur', label: 'Monsieur' },
  { value: 'madame', label: 'Madame' },
];

const REQUIRED_MESSAGE = 'Ce champ est obligatoire.';

const validationSchema = Yup.object().shape({
  civilite: Yup.string().required(REQUIRED_MESSAGE),
  prenom: Yup.string().trim().min(2, 'Le prénom doit comporter au moins 2 caractères.').required(REQUIRED_MESSAGE),
  nom: Yup.string().trim().min(2, 'Le nom doit comporter au moins 2 caractères.').required(REQUIRED_MESSAGE),
  email: Yup.string().email('L’adresse e-mail n’est pas valide.').required(REQUIRED_MESSAGE),
  telephone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-7,9](?:[\s.-]*\d{2}){4}$/,
      'Le téléphone doit être au format 01 02 03 04 05 et ne doit pas commencer par \'08\'',
    )
    .required(REQUIRED_MESSAGE),
});

type ContactStepProps = {
  initialValues: ContactInfo;
  onSubmit: (values: ContactInfo) => void;
  onBack: () => void;
};

const ContactStep: React.FC<ContactStepProps> = ({ initialValues, onSubmit, onBack }) => (
  <Panel>
    <StepTitle>Vos coordonnées</StepTitle>
    <StepText>
      Elles nous permettent de vous transmettre votre dossier et de vous tenir informé(e) de son
      avancement.
    </StepText>
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({
        values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched,
      }) => (
        <FormikForm>
          <Fields>
            <FullWidthRow>
              <FieldLabelText hasError={Boolean(errors.civilite && touched.civilite)}>
                Civilité*
              </FieldLabelText>
              <CiviliteOptions role="radiogroup" aria-label="Civilité">
                {CIVILITE_OPTIONS.map((option) => (
                  <CiviliteOption key={option.value} isSelected={values.civilite === option.value}>
                    <input
                      type="radio"
                      name="civilite"
                      value={option.value}
                      checked={values.civilite === option.value}
                      onChange={() => {
                        setFieldValue('civilite', option.value);
                        setFieldTouched('civilite', true, false);
                      }}
                    />
                    {option.label}
                  </CiviliteOption>
                ))}
              </CiviliteOptions>
              <FieldError>{touched.civilite && errors.civilite}</FieldError>
            </FullWidthRow>
            <Field
              label="Prénom*"
              placeholder="Camille"
              name="prenom"
              autoComplete="given-name"
              value={values.prenom}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.prenom ? !errors.prenom : null}
              errorMessage={touched.prenom ? errors.prenom : ''}
            />
            <Field
              label="Nom*"
              placeholder="Durand"
              name="nom"
              autoComplete="family-name"
              value={values.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.nom ? !errors.nom : null}
              errorMessage={touched.nom ? errors.nom : ''}
            />
            <Field
              label="Adresse e-mail*"
              placeholder="camille.durand@email.fr"
              type="email"
              name="email"
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.email ? !errors.email : null}
              errorMessage={touched.email ? errors.email : ''}
            />
            <Field
              label="Téléphone*"
              placeholder="06 05 04 03 02"
              type="tel"
              name="telephone"
              autoComplete="tel"
              value={values.telephone}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.telephone ? !errors.telephone : null}
              errorMessage={touched.telephone ? errors.telephone : ''}
            />
          </Fields>
          <Reassurance>Aucun paiement à cette étape.</Reassurance>
          <Actions>
            <Btn
              type="button"
              name="demarche-funnel-contact-back"
              text="Retour"
              outlined
              block
              onClick={onBack}
            />
            <Btn type="submit" name="demarche-funnel-contact" text="Continuer" block />
          </Actions>
        </FormikForm>
      )}
    </Formik>
  </Panel>
);

export { ContactStep, CIVILITE_OPTIONS };
